'use client';

import React, { useState, useEffect } from 'react';
import { ArrowUp } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

interface BackToTopProps {
  /**
   * Threshold in pixels to show the button
   * @default 300
   */
  threshold?: number;
  /**
   * Additional class name for the button
   */
  className?: string;
  /**
   * Whether to show the button
   * @default true
   */
  show?: boolean;
}

const BackToTop: React.FC<BackToTopProps> = ({ threshold = 300, className, show = true }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      const scrollY = window.scrollY;
      if (scrollY > threshold) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    // Check initial scroll position
    toggleVisibility();

    window.addEventListener('scroll', toggleVisibility);

    return () => {
      window.removeEventListener('scroll', toggleVisibility);
    };
  }, [threshold]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  if (!show || !isVisible) {
    return null;
  }

  return (
    <Button
      onClick={scrollToTop}
      variant="outline"
      size="icon"
      className={cn(
        'fixed right-4 bottom-6 z-[60] h-12 w-12 rounded-full shadow-lg transition-all duration-300 ease-in-out',
        'hover:scale-110 hover:shadow-xl',
        'bg-background/80 border-border/50 backdrop-blur-sm',
        'hover:bg-accent hover:text-accent-foreground',
        'print:hidden',
        className
      )}
      aria-label="Back to top"
      title="Back to top"
    >
      <ArrowUp />
    </Button>
  );
};

export default BackToTop;
